import { Alert } from 'antd'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { CheckCircleTwoTone } from '@ant-design/icons'

import { getOrderItemPageKey } from 'sections/LKSection/modules/OrderModule/utils'
import { Href } from 'components/ui/Href/Href'
import { Flex, Image, Rate, Text, Title } from 'components/uiKit'
import { getAltFromSrc } from 'utils/helpers/getAltFromSrc/getAltFromSrc'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { DATE_FORMAT_WITH_TIME } from 'shared/constants/values'

import { TicketKeys, ticketStatusTranslator } from '../../../data'
import { Ticket } from '../../../stores/entity/Ticket'
import { TicketStatus } from '../../../types'
import { getTicketPage } from '../../../utils'

import s from './TicketInfo.module.scss'

type TicketInfoProps = {
  ticket: Ticket
}

const getKey = (key: string) => `${TicketKeys.ticketItemPage}.${key}`

export const TicketInfo = ({ ticket }: TicketInfoProps) => {
  const { t } = useTranslation()

  const description = ticket.description.split('\n').filter(Boolean).join('\n')
  const photos = [...ticket.imageUrls, ...ticket.documentUrls]

  return (
    <Flex vertical gap={16} align="start">
      {ticket.status === TicketStatus.closed && (
        <Alert
          message={
            <Flex align="center" gap={8}>
              <CheckCircleTwoTone twoToneColor="#52c41a" />
              {t(getKey('Решено'))} {ticket.finishDate ? dayjs(ticket.finishDate).format(DATE_FORMAT_WITH_TIME) : ''}
            </Flex>
          }
          type="success"
        />
      )}

      <Flex vertical gap={8}>
        <Title level={5}>{t(getOrderItemPageKey('Общая информация'))}</Title>

        {ticket.status !== TicketStatus.closed && (
          <Flex vertical>
            <Text strong>{t(getSharedKey('status'))}:</Text>
            <Text>{t(getTicketPage(ticketStatusTranslator[ticket.status].title))}</Text>
          </Flex>
        )}

        {ticket.orderId && (
          <Flex vertical>
            <Text strong>{t(getKey('Заявка'))}:</Text>
            <Text>{ticket.orderId}</Text>
          </Flex>
        )}

        <Flex vertical>
          <Text strong>{t(getKey('Создан'))}:</Text>
          <Text>{dayjs(ticket.createDate).format(DATE_FORMAT_WITH_TIME)}</Text>
        </Flex>

        <Flex vertical>
          <Text strong>{t(getSharedKey('description'))}:</Text>
          <Text className={s.description}>{description}</Text>
        </Flex>

        {ticket.ticketGrade !== 0 && (
          <Flex vertical>
            <Text strong>{t(getKey('Ваша оценка'))}:</Text>
            <Rate value={ticket.ticketGrade} disabled />
            <Text>{ticket.gradeComment}</Text>
          </Flex>
        )}
      </Flex>

      {(photos.length > 0 || ticket.videoUrls.length > 0) && (
        <>
          <Title level={5}>{t(getSharedKey('attachedFiles'))}</Title>

          <Flex wrap gap={8}>
            {photos.map((file) => (
              <Image key={file} height={200} src={file} alt={getAltFromSrc(file)} />
            ))}
          </Flex>

          <Flex vertical>
            {ticket.videoUrls.map((file) => (
              <Href key={file} href={file} linkType="search">
                {t(getSharedKey('videoURL'))}
              </Href>
            ))}
          </Flex>
        </>
      )}
    </Flex>
  )
}
